import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  acknowledgeTenantPolicyVersion,
  approveTenantPolicyVersion,
  createTenantPolicyDraft,
  deleteTenantPolicyVersionDocument,
  getTenantPolicy,
  getTenantPolicyAcknowledgementStatus,
  getTenantPolicyVersionDocumentUrl,
  listTenantPolicyVersionApprovals,
  listTenantPolicyVersions,
  publishTenantPolicyVersion,
  rejectTenantPolicyVersion,
  retireTenantPolicy,
  submitTenantPolicyVersion,
  uploadTenantPolicyVersionDocument,
  updateTenantPolicyDraft,
  updateTenantPolicySettings,
} from "../../api/tenantPolicies.api";
import PolicyDocumentModal from "../../components/policies/PolicyDocumentModal";
import { listUsers } from "../../api/users.api";
import Loader from "../../components/common/Loader";
import Button from "../../ui/Button";
import PinButton from "../../components/pins/PinButton";
import Input from "../../ui/Input";
import Modal from "../../ui/Modal";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import Textarea from "../../ui/Textarea";
import { spacing } from "../../ui/tokens";
import { useAuth } from "../../useAuth";
import { RoleEnum, hasRole } from "../../constants/roles";
import useRegisterRecentView from "../../features/recentViews/useRegisterRecentView";

const emptyDraftForm = {
  title: "",
  summary: "",
  body: "",
  change_note: "",
  effective_start_at: "",
  effective_end_at: "",
};

const emptyReviewForm = {
  review_frequency: "",
  next_review_date: "",
  last_reviewed_at: "",
  last_reviewed_by: "",
  review_notes: "",
};

const POLICY_DOCUMENT_ACCEPT = ".pdf,.doc,.docx";

const formatBytes = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const isPdfDocument = (filename, mimeType) =>
  String(mimeType || "").toLowerCase() === "application/pdf" || /\.pdf$/i.test(String(filename || ""));

const describeDocumentType = (filename, mimeType) => {
  if (isPdfDocument(filename, mimeType)) return "PDF";
  if (/\.docx?$/i.test(String(filename || ""))) return "Word";
  return mimeType || "Document";
};

const documentCardStyle = {
  display: "grid",
  gap: 8,
  padding: spacing.md,
  borderRadius: 12,
  border: "1px solid #dbe4f0",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
};

const REVIEW_FREQUENCY_OPTIONS = [
  { value: "", label: "Select frequency" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semiannual" },
  { value: "annual", label: "Annual" },
  { value: "30", label: "Every 30 days" },
  { value: "60", label: "Every 60 days" },
  { value: "90", label: "Every 90 days" },
  { value: "180", label: "Every 180 days" },
  { value: "365", label: "Every 365 days" },
];

const isBareNotFoundDetail = (detail) => {
  const normalized = String(detail || "")
    .trim()
    .toLowerCase();
  return !normalized || normalized === "not found" || normalized === "request failed";
};

const extractErrorMessage = (err, fallback = "Request failed") => {
  const requestPath = String(err?.path || err?.url || "");
  const detail =
    err?.detail ??
    err?.message ??
    err?.response?.data?.detail ??
    err?.responseBody?.detail ??
    "";

  if (
    Number(err?.status) === 404 &&
    requestPath.includes("/tenant/policies/") &&
    requestPath.includes("/document") &&
    isBareNotFoundDetail(detail)
  ) {
    return "The running API does not expose the policy document endpoint yet. Restart the backend on the updated code and try again.";
  }
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  const message = err?.message;
  if (typeof message === "string" && message.trim() && message.trim().toLowerCase() !== "request failed") {
    return message.trim();
  }
  const nestedDetail = err?.response?.data?.detail;
  if (typeof nestedDetail === "string" && nestedDetail.trim()) return nestedDetail.trim();
  return fallback;
};

const formatDate = (value, includeTime = false) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return includeTime ? parsed.toLocaleString() : parsed.toLocaleDateString();
};

const isoToLocalInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const offsetMs = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16);
};

const localInputToIso = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const currentLocalDateTimeInput = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 16);
};

const StatusIndicator = ({ status }) => {
  const normalized = typeof status === "string" ? status.toLowerCase() : "";
  const isRetired = normalized === "retired";
  return (
    <StatusBadge
      variant="kpi"
      value={isRetired ? "stale" : "green"}
      label={isRetired ? "Retired" : "Active"}
    />
  );
};

const VersionStatusBadge = ({ status }) => {
  const normalized = typeof status === "string" ? status.toLowerCase() : "draft";
  const valueMap = {
    published: "published",
    draft: "draft",
    submitted: "pending",
    rejected: "overdue",
    retired: "archived",
  };
  return <StatusBadge value={valueMap[normalized] || "draft"} label={normalized.toUpperCase()} />;
};

export default function TenantPolicyDetail({
  tenantPolicyId: tenantPolicyIdProp = null,
  inline = false,
  onRequestClose,
  onPolicyUpdated,
}) {
  const { user } = useAuth() || {};
  const { tenantPolicyId: routeTenantPolicyId } = useParams();
  const navigate = useNavigate();
  const tenantPolicyId = tenantPolicyIdProp ?? routeTenantPolicyId;
  const [policy, setPolicy] = useState(null);
  const [versions, setVersions] = useState([]);
  const [latestDraftApprovals, setLatestDraftApprovals] = useState([]);
  const [ackStatus, setAckStatus] = useState(null);
  const [draftForm, setDraftForm] = useState(emptyDraftForm);
  const [showRetireForm, setShowRetireForm] = useState(false);
  const [retireReason, setRetireReason] = useState("");
  const [ownerInput, setOwnerInput] = useState("");
  const [reviewForm, setReviewForm] = useState(emptyReviewForm);
  const [reviewerOptions, setReviewerOptions] = useState([]);
  const [decisionIntent, setDecisionIntent] = useState(null);
  const [decisionComment, setDecisionComment] = useState("");
  const [decisionError, setDecisionError] = useState(null);
  const [showPublishReviewPrompt, setShowPublishReviewPrompt] = useState(false);
  const [publishReviewError, setPublishReviewError] = useState(null);
  const [draftDocumentFile, setDraftDocumentFile] = useState(null);
  const [draftDocumentInputKey, setDraftDocumentInputKey] = useState(0);
  const [viewerVersion, setViewerVersion] = useState(null);
  const [busyAction, setBusyAction] = useState("");
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!tenantPolicyId) {
      setLoading(false);
      setPolicy(null);
      setVersions([]);
      setAckStatus(null);
      setError("No policy selected.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [detail, policyVersions] = await Promise.all([
        getTenantPolicy(tenantPolicyId),
        listTenantPolicyVersions(tenantPolicyId),
      ]);
      setPolicy(detail || null);
      setVersions(Array.isArray(policyVersions) ? policyVersions : []);

      try {
        const status = await getTenantPolicyAcknowledgementStatus(tenantPolicyId);
        setAckStatus(status || null);
      } catch (_ackError) {
        setAckStatus(null);
      }
    } catch (err) {
      setError(err?.message || "Failed to load policy");
    } finally {
      setLoading(false);
    }
  }, [tenantPolicyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const latestDraft = useMemo(() => {
    const drafts = versions.filter((version) => String(version.status).toLowerCase() === "draft");
    if (drafts.length === 0) return null;
    return drafts.sort((a, b) => Number(b.version || 0) - Number(a.version || 0))[0];
  }, [versions]);

  const latestSubmitted = useMemo(() => {
    const submitted = versions.filter((version) => String(version.status).toLowerCase() === "submitted");
    if (submitted.length === 0) return null;
    return submitted.sort((a, b) => Number(b.version || 0) - Number(a.version || 0))[0];
  }, [versions]);

  const latestRejected = useMemo(() => {
    const rejected = versions.filter((version) => String(version.status).toLowerCase() === "rejected");
    if (rejected.length === 0) return null;
    return rejected.sort((a, b) => Number(b.version || 0) - Number(a.version || 0))[0];
  }, [versions]);

  const publishedVersion = useMemo(() => {
    if (!policy?.published_version_id) return null;
    return versions.find((version) => Number(version.id) === Number(policy.published_version_id)) || null;
  }, [policy?.published_version_id, versions]);

  const isAdmin = hasRole(user?.role, [RoleEnum.ADMIN]);
  const isAdminOrManager = hasRole(user?.role, [RoleEnum.ADMIN, RoleEnum.MANAGER]);

  useEffect(() => {
    let cancelled = false;
    if (!isAdminOrManager) {
      setReviewerOptions([]);
      return () => {
        cancelled = true;
      };
    }

    const loadUsers = async () => {
      try {
        const response = await listUsers();
        const users = Array.isArray(response?.data) ? response.data : [];
        const options = users
          .map((entry) => {
            const username = String(entry?.username || "").trim();
            if (!username) return null;
            const email = String(entry?.email || "").trim();
            return {
              value: username,
              label: email ? `${username} (${email})` : username,
            };
          })
          .filter(Boolean);
        if (!cancelled) {
          setReviewerOptions(options);
        }
      } catch (_err) {
        if (!cancelled) {
          setReviewerOptions([]);
        }
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [isAdminOrManager]);

  useEffect(() => {
    let cancelled = false;
    if (!tenantPolicyId || !latestDraft?.id || !isAdminOrManager) {
      setLatestDraftApprovals([]);
      return () => {
        cancelled = true;
      };
    }

    const loadApprovals = async () => {
      try {
        const approvals = await listTenantPolicyVersionApprovals(tenantPolicyId, latestDraft.id);
        if (!cancelled) {
          setLatestDraftApprovals(Array.isArray(approvals) ? approvals : []);
        }
      } catch (_err) {
        if (!cancelled) {
          setLatestDraftApprovals([]);
        }
      }
    };

    loadApprovals();
    return () => {
      cancelled = true;
    };
  }, [isAdminOrManager, latestDraft?.id, tenantPolicyId]);

  useEffect(() => {
    if (!latestDraft) {
      setDraftForm(emptyDraftForm);
      return;
    }
    setDraftForm({
      title: latestDraft.title || policy?.title || policy?.name || "",
      summary: latestDraft.summary || "",
      body: latestDraft.body || "",
      change_note: latestDraft.change_note || "",
      effective_start_at: isoToLocalInput(latestDraft.effective_start_at),
      effective_end_at: isoToLocalInput(latestDraft.effective_end_at),
    });
  }, [latestDraft, policy]);

  useEffect(() => {
    setOwnerInput(policy?.owner_id || "");
  }, [policy?.owner_id]);

  useEffect(() => {
    setReviewForm({
      review_frequency: policy?.review_frequency || "",
      next_review_date: isoToLocalInput(policy?.next_review_date),
      last_reviewed_at: isoToLocalInput(policy?.last_reviewed_at),
      last_reviewed_by: policy?.last_reviewed_by || "",
      review_notes: policy?.review_notes || "",
    });
  }, [
    policy?.last_reviewed_at,
    policy?.last_reviewed_by,
    policy?.next_review_date,
    policy?.review_frequency,
    policy?.review_notes,
  ]);

  useEffect(() => {
    if (!latestSubmitted) {
      setDecisionIntent(null);
      setDecisionComment("");
      setDecisionError(null);
    }
  }, [latestSubmitted]);

  const metadata = useMemo(() => {
    if (!policy) return [];
    return [
      { label: "Owner", value: policy.owner_id || "Unassigned" },
      { label: "Approval workflow", value: policy.approval_required ? "Required" : "Not required" },
      { label: "Review frequency", value: policy.review_frequency || "-" },
      { label: "Next review date", value: formatDate(policy.next_review_date) },
      {
        label: "Published version",
        value: policy.published_version_exists
          ? policy.published_version_number
            ? `v${policy.published_version_number}`
            : "Published"
          : "Unpublished",
      },
      { label: "Published at", value: formatDate(policy.published_at, true) },
      { label: "Effective start", value: formatDate(policy.effective_start_at, true) },
      { label: "Last reviewed at", value: formatDate(policy.last_reviewed_at, true) },
      { label: "Last reviewed by", value: policy.last_reviewed_by || "-" },
    ];
  }, [policy]);

  const runAction = async (actionName, fn) => {
    setBusyAction(actionName);
    setError(null);
    setMessage(null);
    try {
      await fn();
      setMessage("Saved.");
      await reload();
      if (typeof onPolicyUpdated === "function") {
        onPolicyUpdated();
      }
      return true;
    } catch (err) {
      setError(extractErrorMessage(err, "Request failed"));
      return false;
    } finally {
      setBusyAction("");
    }
  };

  const handleCreateDraft = () =>
    runAction("create-draft", async () => {
      await createTenantPolicyDraft(tenantPolicyId);
    });

  const handleSaveDraft = async () => {
    const success = await runAction("save-draft", async () => {
      if (!latestDraft?.id) return;
      await updateTenantPolicyDraft(tenantPolicyId, latestDraft.id, {
        title: draftForm.title || null,
        summary: draftForm.summary || null,
        body: draftForm.body || null,
        change_note: draftForm.change_note || null,
        effective_start_at: localInputToIso(draftForm.effective_start_at),
        effective_end_at: localInputToIso(draftForm.effective_end_at),
      });
      if (draftDocumentFile instanceof File) {
        await uploadTenantPolicyVersionDocument(tenantPolicyId, latestDraft.id, draftDocumentFile);
      }
    });
    if (success && draftDocumentFile instanceof File) {
      setDraftDocumentFile(null);
      setDraftDocumentInputKey((prev) => prev + 1);
    }
  };

  const openPublishReviewPrompt = () => {
    if (!latestDraft?.id) return;
    setPublishReviewError(null);
    setReviewForm((prev) => ({
      ...prev,
      last_reviewed_at: prev.last_reviewed_at || currentLocalDateTimeInput(),
      last_reviewed_by: prev.last_reviewed_by || String(user?.username || "").trim(),
    }));
    setShowPublishReviewPrompt(true);
  };

  const closePublishReviewPrompt = () => {
    if (busyAction !== "") return;
    setShowPublishReviewPrompt(false);
    setPublishReviewError(null);
  };

  const handlePublishDraft = async () => {
    const reviewNotes = reviewForm.review_notes.trim();
    if (!reviewNotes) {
      setPublishReviewError("Provide a recent review note before publishing.");
      return;
    }

    const reviewedAt = reviewForm.last_reviewed_at || currentLocalDateTimeInput();
    const success = await runAction("publish-draft", async () => {
      if (!latestDraft?.id) return;
      await updateTenantPolicySettings(tenantPolicyId, {
        approval_required: Boolean(policy?.approval_required),
        owner_id: policy?.owner_id || null,
        review_frequency: reviewForm.review_frequency.trim() || null,
        next_review_date: localInputToIso(reviewForm.next_review_date),
        last_reviewed_at: localInputToIso(reviewedAt),
        last_reviewed_by: reviewForm.last_reviewed_by.trim() || null,
        review_notes: reviewNotes,
      });
      await publishTenantPolicyVersion(tenantPolicyId, latestDraft.id);
    });

    if (success) {
      setShowPublishReviewPrompt(false);
      setPublishReviewError(null);
    }
  };

  const handleSubmitDraft = () =>
    runAction("submit-draft", async () => {
      if (!policy?.approval_required) {
        throw new Error("Enable approval workflow before submitting a draft.");
      }
      if (!latestDraft?.id) {
        throw new Error("No draft available to submit.");
      }
      await submitTenantPolicyVersion(tenantPolicyId, latestDraft.id);
    });

  const resetDecisionForm = () => {
    setDecisionIntent(null);
    setDecisionComment("");
    setDecisionError(null);
  };

  const openDecisionForm = (intent) => {
    setDecisionIntent(intent);
    setDecisionComment("");
    setDecisionError(null);
  };

  const handleDecisionConfirm = async () => {
    if (!latestSubmitted?.id || !decisionIntent) return;
    const normalizedComment = decisionComment.trim();
    if (decisionIntent === "reject" && !normalizedComment) {
      setDecisionError("Rejection comment is required.");
      return;
    }
    setDecisionError(null);
    const actionName = decisionIntent === "approve" ? "approve-draft" : "reject-draft";
    const success = await runAction(actionName, async () => {
      if (decisionIntent === "approve") {
        await approveTenantPolicyVersion(tenantPolicyId, latestSubmitted.id, normalizedComment || null);
      } else {
        await rejectTenantPolicyVersion(tenantPolicyId, latestSubmitted.id, normalizedComment);
      }
    });
    if (success) {
      resetDecisionForm();
    }
  };

  const handleToggleApprovalRequired = () =>
    runAction("toggle-approval-required", async () => {
      if (!policy) return;
      await updateTenantPolicySettings(tenantPolicyId, {
        approval_required: !Boolean(policy.approval_required),
      });
    });

  const handleSaveOwner = () =>
    runAction("save-owner", async () => {
      if (!policy) return;
      await updateTenantPolicySettings(tenantPolicyId, {
        approval_required: Boolean(policy.approval_required),
        owner_id: ownerInput.trim() || null,
      });
    });

  const handleSaveReviewMetadata = () =>
    runAction("save-review-metadata", async () => {
      if (!policy) return;
      await updateTenantPolicySettings(tenantPolicyId, {
        approval_required: Boolean(policy.approval_required),
        review_frequency: reviewForm.review_frequency.trim() || null,
        next_review_date: localInputToIso(reviewForm.next_review_date),
        last_reviewed_at: localInputToIso(reviewForm.last_reviewed_at),
        last_reviewed_by: reviewForm.last_reviewed_by.trim() || null,
        review_notes: reviewForm.review_notes.trim() || null,
      });
    });

  const handleRetirePolicy = () =>
    runAction("retire-policy", async () => {
      const reason = retireReason.trim();
      if (!reason) throw new Error("Retire reason is required.");
      await retireTenantPolicy(tenantPolicyId, { reason });
      setRetireReason("");
      setShowRetireForm(false);
    });

  const handleAcknowledge = () =>
    runAction("acknowledge", async () => {
      if (!policy?.published_version_id) throw new Error("No published version to acknowledge.");
      await acknowledgeTenantPolicyVersion(tenantPolicyId, policy.published_version_id, "ui");
    });

  const handleDeleteDraftDocument = async () => {
    const success = await runAction("delete-draft-document", async () => {
      if (!latestDraft?.id) {
        throw new Error("No draft available to delete from.");
      }
      if (!latestDraft?.document_filename) {
        throw new Error("No document uploaded for this draft.");
      }
      await deleteTenantPolicyVersionDocument(tenantPolicyId, latestDraft.id);
    });
    if (success) {
      setDraftDocumentFile(null);
      setDraftDocumentInputKey((prev) => prev + 1);
      if (Number(viewerVersion?.id) === Number(latestDraft?.id)) {
        setViewerVersion(null);
      }
    }
  };

  const openDocumentViewer = (version) => {
    if (!version?.document_filename) return;
    setViewerVersion(version);
  };

  const hasPublishedVersion = Boolean(policy?.published_version_exists);
  const draftHasDocument = Boolean(latestDraft?.document_filename);
  useRegisterRecentView({
    itemType: "tenant_policy",
    itemId: policy?.tenant_policy_id || tenantPolicyId,
    title: policy?.name,
    secondaryText: policy?.policy_status || null,
    enabled: Boolean(policy?.tenant_policy_id && policy?.name),
  });
  const hasApprovedDecisionForLatestDraft = latestDraftApprovals.some(
    (approval) => String(approval?.decision || "").toLowerCase() === "approved"
  );
  const canCreateDraft =
    Boolean(policy && String(policy?.policy_status).toLowerCase() !== "retired") && isAdminOrManager;
  const canRetire = hasPublishedVersion && canCreateDraft && isAdmin;
  const canSubmitDraft = Boolean(
    policy?.approval_required && latestDraft && String(latestDraft.status).toLowerCase() === "draft" && isAdminOrManager
  );
  const canApproveOrReject = Boolean(latestSubmitted && isAdminOrManager);
  const canPublishDraft = Boolean(
    latestDraft &&
      isAdminOrManager &&
      String(latestDraft?.change_note || "").trim().length > 0 &&
      (!policy?.approval_required || hasApprovedDecisionForLatestDraft)
  );
  const currentLifecycleStatus = useMemo(() => {
    if (String(policy?.policy_status || "").toLowerCase() === "retired") return "retired";
    if (latestSubmitted) return "submitted";
    if (latestDraft) return "draft";
    if (latestRejected) return "rejected";
    if (hasPublishedVersion) return "published";
    return "draft";
  }, [hasPublishedVersion, latestDraft, latestRejected, latestSubmitted, policy?.policy_status]);
  const hasUnacknowledgedPublishedVersion =
    hasPublishedVersion &&
    (!ackStatus ||
      Number(policy?.published_version_id) !== Number(ackStatus?.published_version_id) ||
      !ackStatus?.acknowledged);

  const handleBackToRegister = () => {
    if (typeof onRequestClose === "function") {
      onRequestClose();
      return;
    }
    navigate("/catalog/policies");
  };

  return (
    <div style={{ display: "grid", gap: spacing.lg }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing.md }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280" }}>
            Policy detail
          </div>
          <h2 style={{ margin: "6px 0 0" }}>{policy?.name || "Policy"}</h2>
        </div>
        <div style={{ display: "flex", gap: spacing.sm, alignItems: "center", flexWrap: "wrap" }}>
          {!inline && policy && (
            <PinButton
              itemType="tenant_policy"
              itemId={policy.tenant_policy_id}
              summary={{
                title: policy.name,
                secondaryText: [
                  policy.published_version_exists && policy.published_version_number
                    ? `Published v${policy.published_version_number}`
                    : "Unpublished",
                  policy.review_frequency,
                ]
                  .filter(Boolean)
                  .join(" | "),
                status: policy.policy_status,
                ownerDisplay: policy.owner_id,
                updatedAt: policy.latest_draft_updated_at || policy.published_at || null,
              }}
            />
          )}
          {!inline && (
            <Button variant="secondary" onClick={handleBackToRegister}>
              Back to register
            </Button>
          )}
        </div>
      </div>

      {loading && <Loader label="Loading policy..." />}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}
      {message && !error && <div style={{ color: "#065f46" }}>{message}</div>}

      {!loading && !error && policy && (
        <>
          <section
            style={{
              display: "grid",
              gap: spacing.md,
              padding: spacing.lg,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#ffffff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: spacing.md, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600 }}>Status</div>
              <StatusIndicator status={policy.policy_status} />
              <VersionStatusBadge status={currentLifecycleStatus} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: spacing.sm, color: "#374151" }}>
              <input
                type="checkbox"
                checked={Boolean(policy.approval_required)}
                onChange={handleToggleApprovalRequired}
                disabled={!isAdminOrManager || busyAction !== ""}
              />
              <span>Require approval before publish</span>
            </label>
            <div style={{ display: "flex", gap: spacing.sm, alignItems: "flex-end", flexWrap: "wrap", maxWidth: 520 }}>
              <Input
                label="Policy owner"
                value={ownerInput}
                disabled={!isAdminOrManager || busyAction !== ""}
                onChange={(event) => setOwnerInput(event.target.value)}
                placeholder="Username or owner identifier"
              />
              <Button variant="secondary" onClick={handleSaveOwner} disabled={!isAdminOrManager || busyAction !== ""}>
                Save Owner
              </Button>
            </div>
            <div
              style={{
                display: "grid",
                gap: spacing.md,
                maxWidth: 760,
                borderTop: "1px solid #e5e7eb",
                paddingTop: spacing.md,
              }}
            >
              <div style={{ fontWeight: 600 }}>Review metadata</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: spacing.md,
                }}
              >
                <Select
                  label="Review frequency"
                  value={reviewForm.review_frequency}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      review_frequency: event.target.value,
                    }))
                  }
                >
                  {REVIEW_FREQUENCY_OPTIONS.map((option) => (
                    <option key={option.value || "empty"} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                  {reviewForm.review_frequency &&
                    !REVIEW_FREQUENCY_OPTIONS.some(
                      (option) => option.value === reviewForm.review_frequency
                    ) && (
                      <option value={reviewForm.review_frequency}>
                        {reviewForm.review_frequency}
                      </option>
                    )}
                </Select>
                <Input
                  label="Next review date"
                  type="datetime-local"
                  value={reviewForm.next_review_date}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      next_review_date: event.target.value,
                    }))
                  }
                />
                <Input
                  label="Last reviewed at"
                  type="datetime-local"
                  value={reviewForm.last_reviewed_at}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      last_reviewed_at: event.target.value,
                    }))
                  }
                />
                <Select
                  label="Last reviewed by"
                  value={reviewForm.last_reviewed_by}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setReviewForm((prev) => ({
                      ...prev,
                      last_reviewed_by: event.target.value,
                    }))
                  }
                >
                  <option value="">Select reviewer</option>
                  {reviewForm.last_reviewed_by &&
                    !reviewerOptions.some((option) => option.value === reviewForm.last_reviewed_by) && (
                      <option value={reviewForm.last_reviewed_by}>{reviewForm.last_reviewed_by}</option>
                    )}
                  {reviewerOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
              <Textarea
                label="Review notes"
                value={reviewForm.review_notes}
                disabled={!isAdminOrManager || busyAction !== ""}
                onChange={(event) =>
                  setReviewForm((prev) => ({
                    ...prev,
                    review_notes: event.target.value,
                  }))
                }
                rows={4}
              />
              <div>
                <Button
                  variant="secondary"
                  onClick={handleSaveReviewMetadata}
                  disabled={!isAdminOrManager || busyAction !== ""}
                >
                  Save Review Metadata
                </Button>
              </div>
            </div>
            <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
              <Button onClick={handleCreateDraft} disabled={!canCreateDraft || busyAction !== ""}>
                Create Draft
              </Button>
              <Button
                variant="secondary"
                onClick={handleSaveDraft}
                disabled={!latestDraft || !isAdminOrManager || busyAction !== ""}
              >
                Save Draft
              </Button>
              {policy.approval_required && isAdminOrManager && (
                <Button variant="secondary" onClick={handleSubmitDraft} disabled={!canSubmitDraft || busyAction !== ""}>
                  Submit Draft
                </Button>
              )}
              {canApproveOrReject && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => openDecisionForm("approve")}
                    disabled={busyAction !== ""}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => openDecisionForm("reject")}
                    disabled={busyAction !== ""}
                  >
                    Reject
                  </Button>
                </>
              )}
              {isAdminOrManager && (
                <Button
                  variant="secondary"
                  onClick={openPublishReviewPrompt}
                  disabled={!canPublishDraft || busyAction !== ""}
                >
                  Publish Draft
                </Button>
              )}
              <Button
                variant="destructive"
                onClick={() => setShowRetireForm((value) => !value)}
                disabled={!canRetire || busyAction !== ""}
              >
                Retire Policy
              </Button>
            </div>
            {canApproveOrReject && decisionIntent && (
              <div style={{ display: "grid", gap: spacing.sm, maxWidth: 640 }}>
                <div style={{ fontWeight: 600 }}>
                  {decisionIntent === "approve" ? "Approve submitted draft" : "Reject submitted draft"}
                </div>
                <Textarea
                  label={
                    decisionIntent === "approve"
                      ? "Approval comment (optional)"
                      : "Rejection comment (required)"
                  }
                  value={decisionComment}
                  disabled={busyAction !== ""}
                  onChange={(event) => setDecisionComment(event.target.value)}
                  rows={3}
                />
                {decisionError && <div style={{ color: "#b91c1c" }}>{decisionError}</div>}
                <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
                  <Button variant="secondary" onClick={handleDecisionConfirm} disabled={busyAction !== ""}>
                    {decisionIntent === "approve" ? "Confirm Approve" : "Confirm Reject"}
                  </Button>
                  <Button variant="secondary" onClick={resetDecisionForm} disabled={busyAction !== ""}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {policy.approval_required && latestDraft && !hasApprovedDecisionForLatestDraft && !latestSubmitted && (
              <div style={{ color: "#6b7280" }}>
                Approval is required. Submit and approve this draft before publishing.
              </div>
            )}
            {latestDraft && String(latestDraft.change_note || "").trim().length === 0 && (
              <div style={{ color: "#b91c1c" }}>Add and save a change note before publishing this version.</div>
            )}
            {latestSubmitted && (
              <div style={{ color: "#6b7280" }}>
                Draft v{latestSubmitted.version} is submitted and waiting for approval.
              </div>
            )}
            {latestRejected && !latestDraft && (
              <div style={{ color: "#b91c1c" }}>
                Latest workflow version was rejected. Create a new draft to continue.
              </div>
            )}
            {showRetireForm && (
              <div style={{ display: "grid", gap: spacing.sm, maxWidth: 520 }}>
                <Input
                  label="Retire reason"
                  value={retireReason}
                  onChange={(event) => setRetireReason(event.target.value)}
                  placeholder="Reason for retirement"
                />
                <div style={{ display: "flex", gap: spacing.sm }}>
                  <Button variant="destructive" onClick={handleRetirePolicy} disabled={busyAction !== ""}>
                    Confirm retire
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowRetireForm(false);
                      setRetireReason("");
                    }}
                    disabled={busyAction !== ""}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: spacing.md,
              }}
            >
              {metadata.map((item) => (
                <div key={item.label} style={{ display: "grid", gap: 4 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {item.label}
                  </div>
                  <div style={{ fontWeight: 600 }}>{item.value ?? "-"}</div>
                </div>
              ))}
            </div>
          </section>

          <section
            style={{
              display: "grid",
              gap: spacing.md,
              padding: spacing.lg,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#ffffff",
            }}
          >
            <div>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6b7280" }}>
                Draft editor
              </div>
              <h3 style={{ margin: "6px 0 0" }}>
                {latestDraft ? `Editing Draft v${latestDraft.version}` : "No draft available"}
              </h3>
            </div>
            {!latestDraft && (
              <div style={{ color: "#6b7280" }}>
                {latestSubmitted
                  ? `Draft v${latestSubmitted.version} is submitted and locked until decision.`
                  : "Create a draft to edit content and publish updates."}
              </div>
            )}
            {latestDraft && (
              <div style={{ display: "grid", gap: spacing.md }}>
                <Input
                  label="Title"
                  value={draftForm.title}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setDraftForm((prev) => ({
                      ...prev,
                      title: event.target.value,
                    }))
                  }
                />
                <Textarea
                  label="Summary"
                  value={draftForm.summary}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setDraftForm((prev) => ({
                      ...prev,
                      summary: event.target.value,
                    }))
                  }
                  rows={4}
                />
                <Textarea
                  label="Body"
                  value={draftForm.body}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setDraftForm((prev) => ({
                      ...prev,
                      body: event.target.value,
                    }))
                  }
                  rows={10}
                />
                <Textarea
                  label="Change note (required before publish)"
                  value={draftForm.change_note}
                  disabled={!isAdminOrManager || busyAction !== ""}
                  onChange={(event) =>
                    setDraftForm((prev) => ({
                      ...prev,
                      change_note: event.target.value,
                    }))
                  }
                  rows={3}
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: spacing.md,
                  }}
                >
                  <Input
                    label="Effective start"
                    type="datetime-local"
                    value={draftForm.effective_start_at}
                    disabled={!isAdminOrManager || busyAction !== ""}
                    onChange={(event) =>
                      setDraftForm((prev) => ({
                        ...prev,
                        effective_start_at: event.target.value,
                      }))
                    }
                  />
                  <Input
                    label="Effective end"
                    type="datetime-local"
                    value={draftForm.effective_end_at}
                    disabled={!isAdminOrManager || busyAction !== ""}
                    onChange={(event) =>
                      setDraftForm((prev) => ({
                        ...prev,
                        effective_end_at: event.target.value,
                      }))
                    }
                  />
                </div>
                <div style={{ display: "grid", gap: spacing.sm }}>
                  <div style={{ fontWeight: 600 }}>Attached policy document</div>
                  {latestDraft.document_filename ? (
                    <button
                      type="button"
                      onClick={() => openDocumentViewer(latestDraft)}
                      style={{
                        ...documentCardStyle,
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.sm, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{latestDraft.document_filename}</div>
                        <div style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 700 }}>Open document</div>
                      </div>
                      <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap", color: "#475569", fontSize: 13 }}>
                        <span>{describeDocumentType(latestDraft.document_filename, latestDraft.document_mime_type)}</span>
                        {latestDraft.document_size_bytes ? <span>{formatBytes(latestDraft.document_size_bytes)}</span> : null}
                      </div>
                    </button>
                  ) : (
                    <div style={{ color: "#6b7280" }}>No file uploaded for this draft yet.</div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: spacing.sm,
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "grid", gap: 6, minWidth: "min(100%, 360px)" }}>
                      <label
                        htmlFor="policy-draft-document"
                        style={{ fontSize: 12, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "#6b7280" }}
                      >
                        Upload PDF or Word file
                      </label>
                      <input
                        key={draftDocumentInputKey}
                        id="policy-draft-document"
                        type="file"
                        accept={POLICY_DOCUMENT_ACCEPT}
                        disabled={!isAdminOrManager || busyAction !== "" || draftHasDocument}
                        onChange={(event) => setDraftDocumentFile(event.target.files?.[0] || null)}
                      />
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {draftHasDocument
                          ? "Delete the current document before uploading a replacement."
                          : "Supports PDF, DOC, and DOCX files. Select a file and press Save Draft to attach it to this version."}
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteDraftDocument}
                      disabled={!isAdminOrManager || busyAction !== "" || !latestDraft?.document_filename}
                    >
                      Delete Document
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </section>

          {hasPublishedVersion && (
            <section
              style={{
                display: "grid",
                gap: spacing.md,
                padding: spacing.lg,
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                background: "#ffffff",
              }}
            >
              <div>
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", color: "#6b7280" }}>
                  Published content
                </div>
                <h3 style={{ margin: "6px 0 0" }}>{policy.title || policy.name}</h3>
              </div>
              {policy.summary && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Summary</div>
                  <div style={{ color: "#374151", lineHeight: 1.6 }}>{policy.summary}</div>
                </div>
              )}
              {policy.body && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Policy body</div>
                  <div style={{ color: "#111827", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{policy.body}</div>
                </div>
              )}
              {publishedVersion?.document_filename && (
                <div style={{ display: "grid", gap: spacing.sm }}>
                  <div style={{ fontWeight: 600 }}>Published document</div>
                  <button
                    type="button"
                    onClick={() => openDocumentViewer(publishedVersion)}
                    style={{
                      ...documentCardStyle,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: spacing.sm, flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{publishedVersion.document_filename}</div>
                      <div style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 700 }}>
                        {isPdfDocument(publishedVersion.document_filename, publishedVersion.document_mime_type)
                          ? "Open preview"
                          : "Open file"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap", color: "#475569", fontSize: 13 }}>
                      <span>{describeDocumentType(publishedVersion.document_filename, publishedVersion.document_mime_type)}</span>
                      {publishedVersion.document_size_bytes ? <span>{formatBytes(publishedVersion.document_size_bytes)}</span> : null}
                    </div>
                  </button>
                </div>
              )}
              {!policy.summary && !policy.body && !publishedVersion?.document_filename && (
                <div style={{ color: "#6b7280" }}>Published content is not available.</div>
              )}
            </section>
          )}

          <section
            style={{
              display: "grid",
              gap: spacing.md,
              padding: spacing.lg,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#ffffff",
            }}
          >
            <div style={{ fontWeight: 600 }}>Acknowledgement</div>
            {!hasPublishedVersion && <div style={{ color: "#6b7280" }}>No published version to acknowledge.</div>}
            {hasPublishedVersion && ackStatus?.acknowledged && (
              <div style={{ color: "#065f46" }}>Acknowledged on {formatDate(ackStatus.acknowledged_at, true)}.</div>
            )}
            {hasPublishedVersion && hasUnacknowledgedPublishedVersion && (
              <div style={{ display: "flex", gap: spacing.sm, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ color: "#6b7280" }}>This published version has not been acknowledged.</div>
                <Button variant="primary" onClick={handleAcknowledge} disabled={busyAction !== ""}>
                  Acknowledge
                </Button>
              </div>
            )}
          </section>

          <section
            style={{
              display: "grid",
              gap: spacing.md,
              padding: spacing.lg,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#ffffff",
            }}
          >
            <div style={{ fontWeight: 600 }}>Version history</div>
            {versions.length === 0 && <div style={{ color: "#6b7280" }}>No versions found.</div>}
            {versions.length > 0 && (
              <div style={{ display: "grid", gap: spacing.sm }}>
                {versions.map((version) => (
                  <div
                    key={version.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      padding: spacing.sm,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: spacing.sm,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "grid", gap: 2 }}>
                      <div style={{ fontWeight: 600 }}>v{version.version}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        Created: {formatDate(version.created_at, true)}
                      </div>
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        Change note: {version.change_note ? version.change_note : "-"}
                      </div>
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        Document: {version.document_filename || "-"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: spacing.sm, alignItems: "center", flexWrap: "wrap" }}>
                      {version.document_filename && (
                        <Button variant="secondary" onClick={() => openDocumentViewer(version)}>
                          View Document
                        </Button>
                      )}
                      <VersionStatusBadge status={version.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
          <PolicyDocumentModal
            open={Boolean(viewerVersion)}
            onClose={() => setViewerVersion(null)}
            documentName={viewerVersion?.document_filename || ""}
            documentMimeType={viewerVersion?.document_mime_type || ""}
            documentSizeBytes={viewerVersion?.document_size_bytes || 0}
            documentUrl={
              viewerVersion?.id ? getTenantPolicyVersionDocumentUrl(tenantPolicyId, viewerVersion.id) : ""
            }
            versionLabel={viewerVersion?.version ? `Version ${viewerVersion.version}` : ""}
          />
          <Modal
            open={showPublishReviewPrompt}
            onClose={closePublishReviewPrompt}
            title="Recent review"
            description="Provide the recent review note that should be recorded with this policy update before publishing."
            size="md"
          >
            <div style={{ display: "grid", gap: spacing.md }}>
              <Input
                label="Reviewed at"
                type="datetime-local"
                value={reviewForm.last_reviewed_at}
                disabled={busyAction !== ""}
                onChange={(event) =>
                  setReviewForm((prev) => ({
                    ...prev,
                    last_reviewed_at: event.target.value,
                  }))
                }
              />
              <Select
                label="Reviewed by"
                value={reviewForm.last_reviewed_by}
                disabled={busyAction !== ""}
                onChange={(event) =>
                  setReviewForm((prev) => ({
                    ...prev,
                    last_reviewed_by: event.target.value,
                  }))
                }
              >
                <option value="">Select reviewer</option>
                {reviewForm.last_reviewed_by &&
                  !reviewerOptions.some((option) => option.value === reviewForm.last_reviewed_by) && (
                    <option value={reviewForm.last_reviewed_by}>{reviewForm.last_reviewed_by}</option>
                  )}
                {reviewerOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Textarea
                label="Recent review note"
                value={reviewForm.review_notes}
                disabled={busyAction !== ""}
                onChange={(event) => {
                  setPublishReviewError(null);
                  setReviewForm((prev) => ({
                    ...prev,
                    review_notes: event.target.value,
                  }));
                }}
                rows={4}
                error={publishReviewError || undefined}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing.sm, flexWrap: "wrap" }}>
                <Button variant="secondary" onClick={closePublishReviewPrompt} disabled={busyAction !== ""}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handlePublishDraft} disabled={busyAction !== ""}>
                  Publish Draft
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
